<template>
  <div class="billing-gloss batch-statements">
    <div class="loading loading-lg mt-2" v-if="loading"></div>
    <div class="cr-panel cr-panel-first" v-else>
      <div class="cr-panel-head">
        <div class="cr-panel-title">
          <fa-icon :icon="['fal', 'file-alt']" class="cr-panel-icon"/>
          Demonstrativos importados
          <span class="bg-count">{{ items.length }}</span>
        </div>
        <div class="cr-panel-right">
          <button class="btn btn-sm btn-gray btn-icon btn-icon-left tooltip tooltip-left"
                  :data-tooltip="importDisabledReason"
                  :disabled="!!importDisabledReason" @click="openImport">
            <fa-icon :icon="['fal', 'upload']"/>Importar demonstrativo
          </button>
        </div>
        <div class="cr-panel-subtitle">
          XML de análise de conta ou de pagamento aplicados a este lote. O arquivo fica guardado
          para auditoria.
        </div>
      </div>
      <div class="empty cr-panel-empty" v-if="items.length === 0">
        <div class="empty-icon"><fa-icon :icon="['fal', 'file-alt']" size="2x"/></div>
        <p class="empty-subtitle">
          Nenhum demonstrativo importado. Envie o XML da operadora para conciliar glosas e
          pagamentos.
        </p>
      </div>
      <div class="cr-table-scroll" v-else>
        <table class="table table-hover">
          <thead>
          <tr>
            <th>Tipo</th>
            <th>Nº</th>
            <th>Operadora</th>
            <th class="hide-sm">Emitido em</th>
            <th>Importado em</th>
            <th class="text-right">Glosas</th>
            <th class="text-right">Liberado</th>
            <th class="text-right hide-sm">Pago</th>
            <th></th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="statement in items" :key="statement.id">
            <td>
              <span class="bg-pill"
                    :class="statement.type === 'payment' ? 'bg-pill-green' : 'bg-pill-blue'">
                {{ typeName(statement.type) }}
              </span>
            </td>
            <td class="text-bold">{{ statement.number }}</td>
            <td>
              {{ statement.operator && statement.operator.name }}
              <small class="text-gray" v-if="statement.operator && statement.operator.registroANS">
                · ANS {{ statement.operator.registroANS }}
              </small>
            </td>
            <td class="hide-sm text-nowrap">{{ statement.issueDate | date }}</td>
            <td class="text-nowrap">
              {{ statement.applied && statement.applied.at | date('datetime') }}
              <small class="text-gray" v-if="statement.importedBy && statement.importedBy.name">
                · {{ statement.importedBy.name }}
              </small>
            </td>
            <td class="text-right">{{ statement.glosses }}</td>
            <td class="text-right tnum">
              {{ statement.totals && statement.totals.released | currency }}
            </td>
            <td class="text-right tnum hide-sm">
              {{ statement.type === 'payment'
                ? $options.filters.currency(statement.totals && statement.totals.paid)
                : '—' }}
            </td>
            <td class="text-right text-nowrap">
              <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                      data-tooltip="Ver conferência" @click="review(statement)">
                <fa-icon :icon="['fal', 'eye']"/>
              </button>
              <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                      data-tooltip="Baixar XML" :class="{ loading: downloadingId === statement.id }"
                      :disabled="downloadingId === statement.id" @click="downloadXml(statement)">
                <fa-icon :icon="['fal', 'cloud-download']"/>
              </button>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>

    <import-statement-modal
      v-if="modal.show"
      :show="modal.show"
      :batch-id="batchId"
      :statement-id="modal.statementId"
      @close="closeImport"
      @respond="respondFromStatement"
    />
    <appeal-response-modal
      v-if="response.show"
      :show="response.show"
      :appeal-id="response.appealId"
      :prefill="response.prefill"
      @close="closeResponse"
    />
  </div>
</template>

<script>
import { IMPORT_BILLING_STATEMENTS } from '@/data/actions/modules/financial';
import { getName as statementTypeName } from '@/data/billing-statement-types';
import ImportStatementModal from '../modals/ImportStatementModal.vue';
import AppealResponseModal from '../modals/AppealResponseModal.vue';

export default {
  components: {
    ImportStatementModal,
    AppealResponseModal,
  },
  props: {
    batchId: {
      type: String,
      required: true,
    },
    batchStatus: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      loading: true,
      downloadingId: null,
      items: [],
      modal: { show: false, statementId: null },
      response: { show: false, appealId: null, prefill: null },
    };
  },
  computed: {
    canImport() {
      return this.$can(IMPORT_BILLING_STATEMENTS);
    },
    importDisabledReason() {
      if (!this.canImport) return 'Sem permissão';
      if (this.batchStatus === 'closed') return 'Envie o lote antes de importar';
      if (this.batchStatus === 'settled') return 'Lote baixado';
      if (this.batchStatus === 'canceled') return 'Lote cancelado';
      return null;
    },
  },
  watch: {
    batchId() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      this.loading = true;
      return this.$http.get('/billing-statements', { params: { batchId: this.batchId, limit: 50 } })
        .then(({ data }) => {
          this.items = data.items || [];
        })
        .catch((e) => {
          this.$toast.error(e);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    typeName(type) {
      return statementTypeName(type);
    },
    openImport() {
      this.modal = { show: true, statementId: null };
    },
    review(statement) {
      this.modal = { show: true, statementId: statement.id };
    },
    closeImport(applied) {
      this.modal = { show: false, statementId: null };
      if (applied) {
        this.$emit('changed');
        this.load();
      }
    },
    respondFromStatement({ appealId, prefill }) {
      this.modal = { show: false, statementId: null };
      this.response = { show: true, appealId, prefill };
    },
    closeResponse(saved) {
      this.response = { show: false, appealId: null, prefill: null };
      if (saved) {
        this.$emit('changed');
        this.load();
      }
    },
    downloadXml(statement) {
      this.downloadingId = statement.id;
      const name = (statement.file && statement.file.name) || `demonstrativo-${statement.number}.xml`;
      return this.$file.download(`/billing-statements/${statement.id}/xml`, {}, { name })
        .catch(() => {})
        .finally(() => {
          this.downloadingId = null;
        });
    },
  },
};
</script>

<style lang="scss">
@import "../gloss.scss";
</style>
